<h3 class="mb-0">{{ 'summarize-measurements.title' | translate }}</h3>
<p class="mb-4">{{ 'summarize-measurements.subtitle' | translate }}</p>

<p-divider />

@if (loading()) {
  <div class="flex justify-center mt-8">
    <i class="fa-solid fa-spinner fa-spin text-3xl text-primary"></i>
  </div>
}

<!-- STEP 1 — SELECT -->
@if (step() === 'select') {
  <p class="font-semibold mb-2">{{ 'summarize-measurements.steps.select' | translate }}</p>
  <p class="text-sm text-surface-500 mb-3">{{ 'summarize-measurements.select-tree-hint' | translate }}</p>
  <p-tree
    [value]="treeNodes()"
    selectionMode="checkbox"
    [(selection)]="selectedNodes"
    styleClass="w-full border-none mb-4"
  >
    <ng-template let-node pTemplate="default">
      <span class="inline-flex items-center gap-2">
        {{ node.label }}
        @if (node.data.type === 'measurement') {
          <p-tag severity="secondary" [value]="'measurement.types.' + node.data.measurementType | translate" />
        }
      </span>
    </ng-template>
  </p-tree>
  <div class="flex justify-center mt-4">
    <button pButton class="w-full md:w-60"
      [disabled]="!selectedNodes.length"
      (click)="goToConfigure()">
      {{ 'summarize-measurements.measurements-button' | translate }}
    </button>
  </div>
}

<!-- STEP 2 — CONFIGURE -->
@if (step() === 'configure') {
  <p class="font-semibold mb-3">{{ 'summarize-measurements.steps.configure' | translate }}</p>

  @for (section of configSections; track section.type) {
    <p-card styleClass="mb-3">
      <ng-template #title>
        <span class="inline-flex items-center gap-2 text-base">
          {{ section.label }}
          <p-tag severity="secondary" [value]="(groupedIds()[section.type]?.length ?? 0) + ''" />
        </span>
      </ng-template>
      <ng-template #content>
        <form [formGroup]="section.form" class="w-full">
          <formly-form
            [form]="section.form"
            [fields]="section.fields"
            [model]="section.model"
            (modelChange)="onConfigChange(section, $event)">
          </formly-form>
        </form>
      </ng-template>
    </p-card>
  }

  @for (type of unsupportedTypes(); track type) {
    <p-message severity="info" styleClass="w-full mb-3">
      {{ 'summarize-measurements.unsupported-type' | translate: { type: typeLabel(type) } }}
    </p-message>
  }

  @if (!configSections.length) {
    <p-message severity="warn" styleClass="w-full">
      {{ 'summarize-measurements.no-supported-types' | translate }}
    </p-message>
  }

  <div class="flex gap-3 justify-center mt-4">
    <button pButton severity="secondary" class="w-full md:w-60" (click)="backToSelect()">
      {{ 'summarize-measurements.back-to-select-button' | translate }}
    </button>
    <button pButton class="w-full md:w-60" [disabled]="!canSummarize()" (click)="runSummary()">
      {{ 'summarize-measurements.summarize-button' | translate }}
    </button>
  </div>
}

<!-- STEP 3 — RESULTS -->
@if (step() === 'results' && !loading()) {
  <div class="flex items-center justify-between mb-3">
    <p class="font-semibold">{{ 'summarize-measurements.steps.results' | translate }}</p>
    <div class="flex gap-2">
      <button pButton [outlined]="true" severity="secondary" (click)="backToConfigure()">
        <i class="fa-solid fa-arrow-left mr-2"></i>{{ 'summarize-measurements.back-to-select-button' | translate }}
      </button>
      <button pButton (click)="exportXlsx()" [disabled]="exporting()">
        <i class="fa-solid mr-2" [class.fa-spinner]="exporting()" [class.fa-spin]="exporting()" [class.fa-file-excel]="!exporting()"></i>{{ 'summarize-measurements.export-xlsx-button' | translate }}
      </button>
    </div>
  </div>

  @for (section of resultSections(); track section.type) {
    <div class="mt-5">
      <div class="flex items-center justify-between mb-2">
        <p class="font-semibold">{{ 'measurement.types.' + section.type | translate }}</p>
        @if (section.supported) {
          <button pButton [text]="true" severity="secondary" size="small" (click)="copySection(section)">
            <i class="fa-solid fa-copy mr-2"></i>{{ 'summarize-measurements.copy-button' | translate }}
          </button>
        }
      </div>

      @if (section.supported) {
        <cosmos-data-table
          [tableColumns]="section.columns ?? []"
          [tableData]="section.data ?? []"
          [isPageable]="(section.data?.length ?? 0) > 10">
        </cosmos-data-table>
      } @else {
        <p-message severity="info" styleClass="w-full">
          {{ 'summarize-measurements.unsupported-type' | translate: { type: typeLabel(section.type) } }}
        </p-message>
      }
    </div>
    <p-divider />
  }
}
